import { FrameworkActionList, FrameworkClass } from './framework.d';
import { FrameworkActionLists, FrameworkClasses } from './frameworkClasses';
export { FrameworkClassPartType, FrameworkClassType } from './framework-types';

export type EnumOptionsMap = Record<string, { value: string; label: string }[]>;

let cached: EnumOptionsMap | null = null;

export function buildEnumOptions(classes: FrameworkClass[], lists: FrameworkActionList[]): EnumOptionsMap {
  // Build once per session
  if (cached) return cached;
  const options: EnumOptionsMap = {};

  // Helper to upsert enum values
  const upsert = (type: string, value: string, label?: string) => {
    const key = (type || '').trim();
    if (!key) return;
    if (!options[key]) options[key] = [];
    const exists = options[key].some((o) => o.value === value);
    if (!exists) options[key].push({ value, label: label ?? value.split('.').slice(-1)[0].replace(/_/g, ' ') });
  };

  // Gather enum-like fields from FrameworkClasses
  for (const cls of classes) {
    const initial = cls.initialization || [];
    for (const init of initial) {
      const initType = String(init.type ?? '');
      if (initType.toLowerCase() === 'enum' && typeof init.value === 'string') {
        upsert(init.class ? init.class.name ?? String(init.type) : String(init.type), String(init.value));
      }
      // some models use named enum types directly (e.g., ReferenceFrame)
      if (initType && /[A-Za-z]/.test(initType) && typeof init.value === 'string') {
        upsert(initType, String(init.value));
      }
    }
    const actions = cls.actions || [];
    for (const action of actions) {
      for (const arg of action.arguments || []) {
        const argValue = (arg as { value?: unknown } | undefined)?.value;
        if ((arg.type || '').toLowerCase() === 'enum' && typeof argValue === 'string') {
          upsert(String(arg.type), String(argValue));
        }
        if (arg.type && /[A-Za-z]/.test(arg.type) && typeof argValue === 'string') {
          upsert(arg.type, String(argValue));
        }
      }
    }
  }

  // Gather group/action enums from FrameworkActionLists if any are typed
  for (const list of lists) {
    for (const action of list.actions || []) {
      for (const arg of action.arguments || []) {
        const argValue = (arg as { value?: unknown } | undefined)?.value;
        if ((arg.type || '').toLowerCase() === 'enum' && typeof argValue === 'string') {
          upsert(String(arg.type), String(argValue));
        }
        if (arg.type && /[A-Za-z]/.test(arg.type) && typeof argValue === 'string') {
          upsert(arg.type, String(argValue));
        }
      }
    }
  }

  upsert('ReferenceFrame', 'ReferenceFrame.FIELD_CENTRIC', 'Field Centric');
  upsert('ReferenceFrame', 'ReferenceFrame.ROBOT_CENTRIC', 'Robot Centric');

  // SwerveLed (DriveBase LED bitmask targets)
  upsert('SwerveLed', 'SwerveLed.REAR_LEFT', 'Rear left');
  upsert('SwerveLed', 'SwerveLed.REAR_RIGHT', 'Rear right');
  upsert('SwerveLed', 'SwerveLed.FRONT_RIGHT', 'Front right');
  upsert('SwerveLed', 'SwerveLed.FRONT_LEFT', 'Front left');
  upsert('SwerveLed', 'SwerveLed.FRONT', 'Front');
  upsert('SwerveLed', 'SwerveLed.REAR', 'Rear');
  upsert('SwerveLed', 'SwerveLed.LEFT', 'Left');
  upsert('SwerveLed', 'SwerveLed.RIGHT', 'Right');
  upsert('SwerveLed', 'SwerveLed.ALL', 'All');

  upsert('DriveType', 'DriveType.SWERVE', 'Swerve');
  upsert('DriveType', 'DriveType.SKID_STEER', 'Skid steer');

  upsert('DriveMode', 'DriveMode.STOP', 'Stop');
  upsert('DriveMode', 'DriveMode.MANUAL', 'Manual');
  upsert('DriveMode', 'DriveMode.COMMAND', 'Command');
  upsert('DriveMode', 'DriveMode.PATH', 'Path');

  upsert('PathCoordinatesMode', 'PathCoordinatesMode.RELATIVE', 'Relative');
  upsert('PathCoordinatesMode', 'PathCoordinatesMode.ABSOLUTE', 'Absolute');
  upsert('PathCoordinatesMode', 'PathCoordinatesMode.SET_POSE_TO_START', 'Set pose to start');

  upsert('SegmentDistanceReference', 'SegmentDistanceReference.FROM_START', 'From start');
  upsert('SegmentDistanceReference', 'SegmentDistanceReference.FROM_END', 'From end');

  upsert('EventMarkerType', 'EventMarkerType.SEGMENT_PERCENT', 'Segment percent');
  upsert('EventMarkerType', 'EventMarkerType.SEGMENT_DISTANCE', 'Segment distance');

  // MeasuredAt (motor vs mechanism output for geared mechanisms)
  upsert('MeasuredAt', 'MeasuredAt.MOTOR', 'Motor');
  upsert('MeasuredAt', 'MeasuredAt.MECHANISM_OUTPUT', 'Mechanism output');

  for (const m of ['DEGREES', 'ROTATIONS', 'RADIANS']) {
    upsert('RotationalPositionUnits', `RotationalPositionUnits.${m}`);
  }
  for (const m of ['DEGREES_PER_SECOND', 'ROTATIONS_PER_MINUTE', 'ROTATIONS_PER_SECOND', 'RADIANS_PER_SECOND']) {
    upsert('RotationalVelocityUnits', `RotationalVelocityUnits.${m}`);
  }
  for (const m of ['INCHES', 'FEET', 'MILLIMETERS', 'CENTIMETERS', 'METERS']) {
    upsert('LinearPositionUnits', `LinearPositionUnits.${m}`);
  }
  for (const m of ['INCHES_PER_SECOND', 'FEET_PER_SECOND', 'MILLIMETERS_PER_SECOND', 'CENTIMETERS_PER_SECOND', 'METERS_PER_SECOND']) {
    upsert('LinearVelocityUnits', `LinearVelocityUnits.${m}`);
  }

  upsert('ControlStickDriveStyle', 'ControlStickDriveStyle.SWERVE_TWO_STICK', 'Swerve two stick');
  upsert('ControlStickDriveStyle', 'ControlStickDriveStyle.SWERVE_TWO_STICK_INVERTED', 'Swerve two stick inverted');
  upsert('ControlStickDriveStyle', 'ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK', 'Skidsteer single stick');
  upsert('ControlStickDriveStyle', 'ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED', 'Skidsteer single stick inverted');
  upsert('ControlStickDriveStyle', 'ControlStickDriveStyle.SKIDSTEER_TWO_STICK', 'Skidsteer two stick');
  upsert('ControlStickDriveStyle', 'ControlStickDriveStyle.SKIDSTEER_TWO_STICK_INVERTED', 'Skidsteer two stick inverted');

  upsert('MotorStopMode', 'MotorStopMode.BRAKE', 'Brake');
  upsert('MotorStopMode', 'MotorStopMode.COAST', 'Coast');
  upsert('MotorStopMode', 'MotorStopMode.HOLD', 'Hold');

  upsert('MotorType', 'MotorType.HIGH_TORQUE', 'High torque');
  upsert('MotorType', 'MotorType.HIGH_SPEED', 'High speed');

  upsert('MotorDirection', 'MotorDirection.FORWARD', 'Forward');
  upsert('MotorDirection', 'MotorDirection.REVERSE', 'Reverse');

  upsert('PrintScreen', 'PrintScreen.DRIVE_BASE', 'Drive base');
  upsert('PrintScreen', 'PrintScreen.CONTROLLER', 'Controller');
  upsert('PrintScreen', 'PrintScreen.BOTH', 'Both');

  // Cache and return
  cached = options;
  return options;
}

export function getEnumOptions(): EnumOptionsMap {
  return cached ?? buildEnumOptions(FrameworkClasses, FrameworkActionLists);
}
